import React, { useState } from 'react';

import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import type { MenuProps } from '@mui/material';
import { Menu, Typography } from '@mui/material';
import { CheckMarkMenuItem, SelectButton } from 'src/components';
import { SmallDownArrowIcon } from 'src/components/icons/SmallDownArrowIcon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  downArrow: {
    height: 12,
    marginLeft: theme.spacing(1),
    width: 12,
  },
  subtitle: {
    margin: theme.spacing(1.625, 0, 1.625, 2),
  },
}));

export enum CommentsFilter {
  All = 'All',
  Resolved = 'Resolved',
  Unresolved = 'Unresolved',
}

export const commentFilterLabelMap: Record<CommentsFilter, string> = {
  [CommentsFilter.All]: 'All',
  [CommentsFilter.Resolved]: 'Resolved',
  [CommentsFilter.Unresolved]: 'Unresolved',
};

export const commentsFilterVar = makeVar<CommentsFilter>(CommentsFilter.Unresolved);
export function useCommentsFilter(): CommentsFilter {
  return useReactiveVar(commentsFilterVar);
}

type CommentsFilterMenuProps = Omit<MenuProps, 'open'>;

export function CommentsFilterMenu(props: CommentsFilterMenuProps): JSX.Element {
  const { classes } = useStyles();
  const commentsFilter = useCommentsFilter();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  function selectFilter(filter: CommentsFilter): void {
    commentsFilterVar(filter);
    setOpen(false);
  }

  return (
    <>
      <SelectButton
        ref={setAnchorEl}
        className={classes.subtitle}
        selected={open}
        onClick={() => setOpen(!open)}
      >
        <Typography variant="subtitle1">{commentFilterLabelMap[commentsFilter]}</Typography>
        <SmallDownArrowIcon className={classes.downArrow} />
      </SelectButton>
      <Menu
        keepMounted
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        open={open}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        variant="menu"
        onClose={() => setOpen(false)}
        {...props}
      >
        {Object.values(CommentsFilter).map((filter, index) => (
          <CheckMarkMenuItem
            key={index}
            selected={filter === commentsFilter}
            onClick={() => selectFilter(filter)}
          >
            {commentFilterLabelMap[filter]}
          </CheckMarkMenuItem>
        ))}
      </Menu>
    </>
  );
}
